/**
 * Flash detect — watch the viewport itself for a disruption, whatever caused it.
 *
 * flash-trace.ts records the seams we KNOW can repaint the whole screen. The
 * founder's flash survived six fixes to those seams (#3776 #3777 #3782 #3783
 * #3784 #3836), so this leaf stops guessing at causes and watches effects:
 *
 *   - `remount`  a big swap of the DOM (≥150 elements out AND ≥100 in inside
 *                one 250 ms window) — the page vanishing and coming back, from
 *                any cause. Counts only; the largest root on each side is
 *                summarised as tag + data-testid + first class.
 *   - `vanish`   one window takes away 40% or more of the body and does not
 *                put it back: a blank or a skeleton that outlives the window.
 *                A window the page was hidden in at ANY moment counts for
 *                neither. Streaming is not a swap: a record that adds AND
 *                removes (innerHTML / replaceChildren; React moves one node per
 *                record) is skipped, and so is anything inside rendered
 *                markdown (.markdown-content, the pill's .bh-answer).
 *   - `shift`    a layout shift ≥ 0.2 nobody's input caused, with its largest
 *                source. Chromium only; WebKit (the desktop shell) has none.
 *   - `reload`   how this page arrived (navigate / reload / back_forward),
 *                and whether the reload was OURS: an automatic reload writes
 *                a sessionStorage breadcrumb first (markAutoReload).
 *   - `visible` / `focus`  the attention changes — a "random" flash usually
 *                lands on a return to the tab or window.
 *
 * Everything lands in the flash-trace ring (and so in the recorder's report).
 * Never text content, never a URL. Cheap enough to leave on: the observer
 * callback is a node-type check and a subtree count per added/removed element.
 */
import { traceFlash } from "@/lib/flash-trace";

/** The breadcrumb an automatic reload leaves for the page it reloads into. */
export const AUTO_RELOAD_KEY = "vestra:auto-reload";

const REMOUNT_WINDOW_MS = 250;
const REMOUNT_MIN_REMOVED = 150;
const REMOUNT_MIN_ADDED = 100;
/** A vanish takes this share of the body in one window… */
const VANISH_SHARE = 0.4;
/** …and at least this many elements, so a near-empty page cannot trip it. */
const VANISH_MIN_REMOVED = 50;
/** Rendered markdown re-renders wholesale as it streams: never a remount. */
const STREAMING_CONTAINERS = ".markdown-content, .bh-answer";
const SHIFT_MIN = 0.2;
/** A breadcrumb older than this belongs to a reload that never happened. */
const AUTO_RELOAD_FRESH_MS = 60_000;
/** Long enough for a test id or a class token, short enough to stay a label. */
const LABEL_MAX = 60;

/** Say so on the line before any reload the person did not ask for. */
export function markAutoReload(reason: string): void {
  try {
    sessionStorage.setItem(
      AUTO_RELOAD_KEY,
      JSON.stringify({ reason, at: Date.now() })
    );
  } catch {
    // Storage refused: the reload reads as his own, which is only a missed report.
  }
}

/** Read and spend the breadcrumb: the reason when fresh, else null. */
function takeAutoReload(): string | null {
  try {
    const raw = sessionStorage.getItem(AUTO_RELOAD_KEY);
    if (raw === null) return null;
    sessionStorage.removeItem(AUTO_RELOAD_KEY);
    const crumb = JSON.parse(raw) as { reason?: unknown; at?: unknown };
    const fresh =
      typeof crumb.at === "number" &&
      Date.now() - crumb.at <= AUTO_RELOAD_FRESH_MS;
    return fresh && typeof crumb.reason === "string" ? crumb.reason : null;
  } catch {
    return null;
  }
}

/** `div[testid=task-pane].flex n=312` — tag, test id, first class, count. */
export function summarizeElement(el: Element, count?: number): string {
  const testid = el.getAttribute("data-testid")?.slice(0, LABEL_MAX);
  const cls = el.classList[0]?.slice(0, LABEL_MAX);
  return (
    el.tagName.toLowerCase() +
    (testid ? `[testid=${testid}]` : "") +
    (cls ? `.${cls}` : "") +
    (count === undefined ? "" : ` n=${count}`)
  );
}

// ── Attention: when did he last return, focus, touch the page ───────────────
// performance.now() units (ms since this page loaded).

let lastVisibleAt: number | null = null;
let lastFocusAt: number | null = null;
let lastNavInputAt: number | null = null;

export interface Attention {
  visible: boolean;
  sinceVisibleMs: number | null;
  sinceFocusMs: number | null;
  /** Since his last NAVIGATING input: a click on a link, button, tab or menu item, or Enter. */
  sinceNavInputMs: number | null;
}

export function readAttention(now = performance.now()): Attention {
  const since = (at: number | null) =>
    at === null ? null : Math.round(now - at);
  return {
    visible: document.visibilityState === "visible",
    sinceVisibleMs: since(lastVisibleAt),
    sinceFocusMs: since(lastFocusAt),
    sinceNavInputMs: since(lastNavInputAt),
  };
}

/** What a click must land inside to be navigation. Wheel, typing and plain page are not. */
const NAVIGATING_TARGETS = "a, button, [role=tab], [role=menuitem]";

function watchAttention(): () => void {
  lastVisibleAt = document.visibilityState === "visible" ? 0 : null;
  lastFocusAt = document.hasFocus() ? 0 : null;
  lastNavInputAt = null;
  const onVisibility = () => {
    const state = document.visibilityState;
    if (state === "visible") lastVisibleAt = performance.now();
    traceFlash("visible", state);
  };
  const onFocus = () => {
    lastFocusAt = performance.now();
    traceFlash("focus", "in");
  };
  const onBlur = () => traceFlash("focus", "out");
  const onClick = (e: Event) => {
    if (e.target instanceof Element && e.target.closest(NAVIGATING_TARGETS))
      lastNavInputAt = performance.now();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter") lastNavInputAt = performance.now();
  };
  const input = { capture: true, passive: true };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", onFocus);
  window.addEventListener("blur", onBlur);
  window.addEventListener("click", onClick, input);
  window.addEventListener("keydown", onKey, input);
  return () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("blur", onBlur);
    window.removeEventListener("click", onClick, input);
    window.removeEventListener("keydown", onKey, input);
  };
}

// ── Remount / vanish: a big change to the DOM inside one window ──────────────

/** `Node.ELEMENT_NODE`, by value: the observer's callback can run where the
 *  `Node` global is gone (a test environment torn down under a live
 *  observer), and a detector must never throw. */
const ELEMENT_NODE = 1;

/** innerHTML / replaceChildren, or a target inside streaming markdown. */
function isStreamingRecord(record: MutationRecord): boolean {
  if (record.addedNodes.length > 0 && record.removedNodes.length > 0)
    return true;
  const target = record.target;
  const el =
    target.nodeType === ELEMENT_NODE
      ? (target as Element)
      : target.parentElement;
  return !!el?.closest(STREAMING_CONTAINERS);
}

function watchRemounts(): () => void {
  let removed = 0;
  let added = 0;
  let bigRemoved: { el: Element; count: number } | null = null;
  let bigAdded: { el: Element; count: number } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // Hidden at any moment of the window: nobody saw all of it, so it is dropped.
  let sawHidden = false;

  const closeWindow = () => {
    timer = null;
    const seen = !sawHidden && document.visibilityState === "visible";
    if (seen && removed >= REMOUNT_MIN_REMOVED && added >= REMOUNT_MIN_ADDED) {
      traceFlash("remount", `-${removed} +${added}`, {
        removed,
        added,
        root_removed: bigRemoved
          ? summarizeElement(bigRemoved.el, bigRemoved.count)
          : "",
        root_added: bigAdded
          ? summarizeElement(bigAdded.el, bigAdded.count)
          : "",
      });
    } else if (seen && removed >= VANISH_MIN_REMOVED) {
      // The body as the window found it: what is left, plus what went, less what came.
      const body =
        document.body.getElementsByTagName("*").length + removed - added;
      if (removed >= VANISH_SHARE * body) {
        traceFlash("vanish", `-${removed} of ${body}`, {
          removed,
          body,
          root_removed: bigRemoved
            ? summarizeElement(bigRemoved.el, bigRemoved.count)
            : "",
        });
      }
    }
    removed = 0;
    added = 0;
    bigRemoved = null;
    bigAdded = null;
    sawHidden = false;
  };

  const onVisibility = () => {
    if (timer !== null && document.visibilityState !== "visible")
      sawHidden = true;
  };

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (isStreamingRecord(record)) continue;
      record.removedNodes.forEach((node) => {
        if (node.nodeType !== ELEMENT_NODE) return;
        const el = node as Element;
        const count = el.getElementsByTagName("*").length + 1;
        removed += count;
        if (!bigRemoved || count > bigRemoved.count) bigRemoved = { el, count };
      });
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== ELEMENT_NODE) return;
        const el = node as Element;
        const count = el.getElementsByTagName("*").length + 1;
        added += count;
        if (!bigAdded || count > bigAdded.count) bigAdded = { el, count };
      });
    }
    if (timer === null && (removed > 0 || added > 0)) {
      sawHidden = document.visibilityState !== "visible";
      timer = setTimeout(closeWindow, REMOUNT_WINDOW_MS);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  document.addEventListener("visibilitychange", onVisibility);
  return () => {
    observer.disconnect();
    document.removeEventListener("visibilitychange", onVisibility);
    if (timer !== null) clearTimeout(timer);
  };
}

// ── Shift: a big layout shift nobody's input caused ─────────────────────────

/** The Layout Instability API entry (not in lib.dom). */
interface LayoutShiftEntry {
  value: number;
  hadRecentInput: boolean;
  sources?: {
    node: Node | null;
    previousRect: { width: number; height: number };
    currentRect: { width: number; height: number };
  }[];
}

function largestSource(entry: LayoutShiftEntry): string {
  let best: Element | null = null;
  let bestArea = -1;
  for (const s of entry.sources ?? []) {
    if (!s.node || s.node.nodeType !== ELEMENT_NODE) continue;
    const area = Math.max(
      s.previousRect.width * s.previousRect.height,
      s.currentRect.width * s.currentRect.height
    );
    if (area > bestArea) {
      best = s.node as Element;
      bestArea = area;
    }
  }
  return best ? summarizeElement(best) : "";
}

function watchShifts(): () => void {
  if (
    typeof PerformanceObserver === "undefined" ||
    !PerformanceObserver.supportedEntryTypes?.includes("layout-shift")
  )
    return () => {};
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries() as unknown as LayoutShiftEntry[]) {
      if (entry.hadRecentInput || entry.value < SHIFT_MIN) continue;
      traceFlash("shift", entry.value.toFixed(3), {
        shift_source: largestSource(entry),
      });
    }
  });
  observer.observe({ type: "layout-shift", buffered: false });
  return () => observer.disconnect();
}

// ── Reload: how this page arrived ───────────────────────────────────────────

function traceArrival(): void {
  let nav = "unknown";
  try {
    const entry = performance.getEntriesByType("navigation")[0] as
      PerformanceNavigationTiming | undefined;
    if (entry?.type) nav = entry.type;
  } catch {
    // No Navigation Timing: the arrival is 'unknown', the breadcrumb still counts.
  }
  const auto = takeAutoReload();
  if (auto) traceFlash("reload", `${nav} auto:${auto}`, { auto });
  else traceFlash("reload", nav);
}

/**
 * Start every detector. Returns the stop (tests; the app leaves it on).
 * Never throws: a detector a browser cannot run is skipped, not fatal.
 */
export function startFlashDetect(): () => void {
  const stops: (() => void)[] = [];
  for (const start of [watchAttention, watchRemounts, watchShifts]) {
    try {
      stops.push(start());
    } catch {
      // A detector this browser cannot run is simply absent.
    }
  }
  traceArrival();
  return () => {
    for (const stop of stops) stop();
  };
}
